import { supabase } from './supabase';

export const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'PL',
  'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
] as const;

export const NON_EU_COUNTRIES = [
  'GB', 'CH', 'NO', 'US', 'CA', 'AU', 'NZ', 'JP', 'CN', 'KR',
  'SG', 'HK', 'TW', 'TH', 'MY', 'ID', 'PH', 'VN', 'IN', 'AE',
  'SA', 'IL', 'TR', 'RU', 'UA', 'BR', 'MX', 'AR', 'CL', 'ZA', 'UY'
] as const;

export type DestinationType = 'domestic' | 'eu' | 'non_eu';

export interface PostalCodePattern {
  pattern: RegExp;
  format: string;
  example: string;
  normalize?: (code: string) => string;
}

export const POSTAL_CODE_PATTERNS: Record<string, PostalCodePattern> = {
  NL: {
    pattern: /^\d{4}\s?[A-Z]{2}$/i,
    format: '1234 AB',
    example: '1234 AB',
    normalize: (code: string) => code.replace(/\s/g, '').toUpperCase(),
  },
  BE: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '1000',
    normalize: (code: string) => code.trim(),
  },
  DE: {
    pattern: /^\d{5}$/,
    format: '12345',
    example: '10115',
    normalize: (code: string) => code.trim(),
  },
  FR: {
    pattern: /^\d{5}$/,
    format: '12345',
    example: '75001',
    normalize: (code: string) => code.trim(),
  },
  LU: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '1234',
    normalize: (code: string) => code.trim(),
  },
  AT: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '1010',
    normalize: (code: string) => code.trim(),
  },
  ES: {
    pattern: /^\d{5}$/,
    format: '12345',
    example: '28001',
    normalize: (code: string) => code.trim(),
  },
  IT: {
    pattern: /^\d{5}$/,
    format: '12345',
    example: '00100',
    normalize: (code: string) => code.trim(),
  },
  PT: {
    pattern: /^\d{4}(-\d{3})?$/,
    format: '1234-567',
    example: '1000-001',
    normalize: (code: string) => code.trim(),
  },
  GB: {
    pattern: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
    format: 'SW1A 1AA',
    example: 'SW1A 1AA',
    normalize: (code: string) => code.toUpperCase().replace(/\s+/g, ' ').trim(),
  },
  CH: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '8001',
    normalize: (code: string) => code.trim(),
  },
  PL: {
    pattern: /^\d{2}-\d{3}$/,
    format: '12-345',
    example: '00-001',
    normalize: (code: string) => code.trim(),
  },
  CZ: {
    pattern: /^\d{3}\s?\d{2}$/,
    format: '123 45',
    example: '110 00',
    normalize: (code: string) => code.replace(/\s/g, ''),
  },
  DK: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '1050',
    normalize: (code: string) => code.trim(),
  },
  SE: {
    pattern: /^\d{3}\s?\d{2}$/,
    format: '123 45',
    example: '111 21',
    normalize: (code: string) => code.replace(/\s/g, ''),
  },
  NO: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '0150',
    normalize: (code: string) => code.trim(),
  },
  FI: {
    pattern: /^\d{5}$/,
    format: '12345',
    example: '00100',
    normalize: (code: string) => code.trim(),
  },
  IE: {
    pattern: /^[A-Z\d]{3}\s?[A-Z\d]{4}$/i,
    format: 'A12 BC34',
    example: 'D02 AF30',
    normalize: (code: string) => code.toUpperCase().replace(/\s+/g, ' ').trim(),
  },
  GR: {
    pattern: /^\d{3}\s?\d{2}$/,
    format: '123 45',
    example: '104 31',
    normalize: (code: string) => code.replace(/\s/g, ''),
  },
  HU: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '1011',
    normalize: (code: string) => code.trim(),
  },
  US: {
    pattern: /^\d{5}(-\d{4})?$/,
    format: '12345 or 12345-6789',
    example: '10001',
    normalize: (code: string) => code.trim(),
  },
  CA: {
    pattern: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i,
    format: 'A1A 1A1',
    example: 'M5V 3L9',
    normalize: (code: string) => code.toUpperCase().replace(/\s+/g, ' ').trim(),
  },
  AU: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '2000',
    normalize: (code: string) => code.trim(),
  },
};

export const COUNTRY_NAMES: Record<string, string> = {
  NL: 'Netherlands',
  BE: 'Belgium',
  DE: 'Germany',
  FR: 'France',
  LU: 'Luxembourg',
  AT: 'Austria',
  ES: 'Spain',
  IT: 'Italy',
  PT: 'Portugal',
  GB: 'United Kingdom',
  CH: 'Switzerland',
  PL: 'Poland',
  CZ: 'Czech Republic',
  DK: 'Denmark',
  SE: 'Sweden',
  NO: 'Norway',
  FI: 'Finland',
  IE: 'Ireland',
  GR: 'Greece',
  HU: 'Hungary',
  US: 'United States',
  CA: 'Canada',
  AU: 'Australia',
  NZ: 'New Zealand',
  JP: 'Japan',
  CN: 'China',
  KR: 'South Korea',
  SG: 'Singapore',
  HK: 'Hong Kong',
};

export const SUPPORTED_COUNTRIES = [
  { code: 'NL', name: 'Netherlands', type: 'domestic' as DestinationType },
  { code: 'BE', name: 'Belgium', type: 'eu' as DestinationType },
  { code: 'DE', name: 'Germany', type: 'eu' as DestinationType },
  { code: 'FR', name: 'France', type: 'eu' as DestinationType },
  { code: 'LU', name: 'Luxembourg', type: 'eu' as DestinationType },
  { code: 'AT', name: 'Austria', type: 'eu' as DestinationType },
  { code: 'ES', name: 'Spain', type: 'eu' as DestinationType },
  { code: 'IT', name: 'Italy', type: 'eu' as DestinationType },
  { code: 'PT', name: 'Portugal', type: 'eu' as DestinationType },
  { code: 'PL', name: 'Poland', type: 'eu' as DestinationType },
  { code: 'CZ', name: 'Czech Republic', type: 'eu' as DestinationType },
  { code: 'DK', name: 'Denmark', type: 'eu' as DestinationType },
  { code: 'SE', name: 'Sweden', type: 'eu' as DestinationType },
  { code: 'FI', name: 'Finland', type: 'eu' as DestinationType },
  { code: 'IE', name: 'Ireland', type: 'eu' as DestinationType },
  { code: 'GR', name: 'Greece', type: 'eu' as DestinationType },
  { code: 'HU', name: 'Hungary', type: 'eu' as DestinationType },
  { code: 'GB', name: 'United Kingdom', type: 'non_eu' as DestinationType },
  { code: 'CH', name: 'Switzerland', type: 'non_eu' as DestinationType },
  { code: 'NO', name: 'Norway', type: 'non_eu' as DestinationType },
  { code: 'US', name: 'United States', type: 'non_eu' as DestinationType },
  { code: 'CA', name: 'Canada', type: 'non_eu' as DestinationType },
  { code: 'AU', name: 'Australia', type: 'non_eu' as DestinationType },
];

export interface PostNLSenderInfo {
  companyName: string;
  firstName?: string;
  lastName?: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  countryCode: string;
  email?: string;
  phone?: string;
}

export interface PostNLReceiverInfo {
  companyName?: string;
  firstName?: string;
  lastName?: string;
  street: string;
  houseNumber: string;
  houseNumberExt?: string;
  postalCode: string;
  city: string;
  countryCode: string;
  region?: string;
  email?: string;
  phone?: string;
}

export interface PostNLLabelOptions {
  serviceType: 'standard' | 'signature' | 'insured' | 'evening' | 'sunday';
  weightGrams: number;
  dimensions?: {
    height: number;
    width: number;
    depth: number;
  };
  labelFormat: 'A4' | 'A6';
  generateReturnLabel?: boolean;
  insuranceAmount?: number;
  reference?: string;
}

export interface PostNLCustomsInfo {
  contentDescription?: string;
  hsCode?: string;
  declaredValue?: number;
  currency?: string;
  originCountry?: string;
  valueSplit?: number;
}

export interface PostNLLabelRequest {
  shipmentId: string;
  senderInfo: PostNLSenderInfo;
  receiverInfo: PostNLReceiverInfo;
  options: PostNLLabelOptions;
  customs?: PostNLCustomsInfo;
}

export interface PostNLErrorResponse {
  error: string;
  code?: string;
  field?: string;
  suggestion?: string;
  postalCodeFormat?: string;
  allErrors?: Array<{
    field: string;
    message: string;
    suggestion?: string;
  }>;
}

export interface PostNLLabelResponse {
  success: boolean;
  barcode?: string;
  trackingCode?: string;
  labelUrl?: string;
  labelPdfBase64?: string;
  destinationType?: DestinationType;
  productCode?: string;
  error?: string;
  errorDetails?: PostNLErrorResponse;
}

export interface ParsedAddress {
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  countryCode: string;
}

export function getDestinationType(countryCode: string): DestinationType {
  const code = countryCode.toUpperCase();
  if (code === 'NL') return 'domestic';
  if (EU_COUNTRIES.includes(code as typeof EU_COUNTRIES[number])) return 'eu';
  return 'non_eu';
}

export function getDestinationLabel(countryCode: string): string {
  const destType = getDestinationType(countryCode);
  switch (destType) {
    case 'domestic':
      return 'Domestic (Netherlands)';
    case 'eu':
      return 'EU';
    case 'non_eu':
      return 'Non-EU / International';
  }
}

export function requiresCustomsDeclaration(countryCode: string): boolean {
  return getDestinationType(countryCode) === 'non_eu';
}

export function parseNLAddress(fullAddress: string): ParsedAddress {
  const lines = fullAddress.split(/[\n,]+/).map(l => l.trim()).filter(Boolean);

  let street = '';
  let houseNumber = '';
  let postalCode = '';
  let city = '';
  let countryCode = 'NL';

  for (const line of lines) {
    const countryMatch = line.match(/^(Netherlands|Nederland|NL|BE|Belgium|Belgie|DE|Germany|Deutschland|FR|France|GB|United Kingdom|UK)$/i);
    if (countryMatch) {
      const countryMap: Record<string, string> = {
        'netherlands': 'NL', 'nederland': 'NL', 'nl': 'NL',
        'belgium': 'BE', 'belgie': 'BE', 'be': 'BE',
        'germany': 'DE', 'deutschland': 'DE', 'de': 'DE',
        'france': 'FR', 'fr': 'FR',
        'united kingdom': 'GB', 'uk': 'GB', 'gb': 'GB',
      };
      countryCode = countryMap[countryMatch[1].toLowerCase()] || 'NL';
      continue;
    }

    const nlPostalMatch = line.match(/\b(\d{4}\s?[A-Z]{2})\s+(.+)/i);
    if (nlPostalMatch) {
      postalCode = nlPostalMatch[1].replace(/\s/g, '').toUpperCase();
      city = nlPostalMatch[2];
      continue;
    }

    const bePostalMatch = line.match(/\b(\d{4})\s+([A-Za-z].+)/);
    if (bePostalMatch && !postalCode) {
      postalCode = bePostalMatch[1];
      city = bePostalMatch[2];
      continue;
    }

    const dePostalMatch = line.match(/\b(\d{5})\s+([A-Za-z].+)/);
    if (dePostalMatch && !postalCode) {
      postalCode = dePostalMatch[1];
      city = dePostalMatch[2];
      countryCode = 'DE';
      continue;
    }

    const streetMatch = line.match(/^(.+?)\s+(\d+\s*[A-Za-z]?\s*(?:bis|[a-z])?)$/i);
    if (streetMatch && !street) {
      street = streetMatch[1];
      houseNumber = streetMatch[2].trim();
      continue;
    }

    if (!street && line.length > 0 && !line.match(/\d{4}/)) {
      street = line;
    }
  }

  return { street, houseNumber, postalCode, city, countryCode };
}

export function extractPostalCode(address: string): string | null {
  const nlPattern = /\b\d{4}\s?[A-Z]{2}\b/i;
  const bePattern = /\b\d{4}\b/;
  const dePattern = /\b\d{5}\b/;

  const nlMatch = address.match(nlPattern);
  if (nlMatch) return nlMatch[0].replace(/\s/g, '').toUpperCase();

  const deMatch = address.match(dePattern);
  if (deMatch) return deMatch[0];

  const beMatch = address.match(bePattern);
  if (beMatch) return beMatch[0];

  return null;
}

export interface PostalCodeValidationResult {
  valid: boolean;
  normalized: string;
  error?: string;
  format?: string;
  example?: string;
}

export function validatePostalCode(postalCode: string, countryCode: string = 'NL'): PostalCodeValidationResult {
  const code = countryCode.toUpperCase();
  const pattern = POSTAL_CODE_PATTERNS[code];

  if (!pattern) {
    return { valid: true, normalized: postalCode.trim() };
  }

  const normalized = pattern.normalize
    ? pattern.normalize(postalCode)
    : postalCode.trim();

  if (!pattern.pattern.test(normalized)) {
    return {
      valid: false,
      normalized,
      error: `Invalid postal code for ${COUNTRY_NAMES[code] || code}. Expected format: ${pattern.format} (e.g., ${pattern.example})`,
      format: pattern.format,
      example: pattern.example,
    };
  }

  return { valid: true, normalized, format: pattern.format, example: pattern.example };
}

export function getPostalCodeHint(countryCode: string): string | null {
  const code = countryCode.toUpperCase();
  const pattern = POSTAL_CODE_PATTERNS[code];
  if (!pattern) return null;
  return `Format: ${pattern.format} (e.g., ${pattern.example})`;
}

export const SERVICE_TYPES = [
  { value: 'standard', label: 'Standard Delivery', description: 'Next business day delivery', availableFor: ['domestic', 'eu', 'non_eu'] },
  { value: 'signature', label: 'Signature Required', description: 'Delivery with signature confirmation', availableFor: ['domestic', 'eu'] },
  { value: 'insured', label: 'Insured Delivery', description: 'With insurance coverage up to EUR 500', availableFor: ['domestic', 'eu'] },
  { value: 'evening', label: 'Evening Delivery', description: 'Delivery between 18:00 - 22:00', availableFor: ['domestic'] },
  { value: 'sunday', label: 'Sunday Delivery', description: 'Delivery on Sunday', availableFor: ['domestic'] },
] as const;

export function getAvailableServiceTypes(countryCode: string) {
  const destType = getDestinationType(countryCode);
  return SERVICE_TYPES.filter(service =>
    (service.availableFor as readonly DestinationType[]).includes(destType)
  );
}

export const LABEL_FORMATS = [
  { value: 'A6', label: 'A6 (Standard Label)', description: '105 x 148 mm' },
  { value: 'A4', label: 'A4 (Full Page)', description: '210 x 297 mm' },
] as const;

export const WEIGHT_PRESETS = [
  { value: 100, label: '100g (Letter)' },
  { value: 500, label: '500g (Small package)' },
  { value: 1000, label: '1kg' },
  { value: 2000, label: '2kg' },
  { value: 5000, label: '5kg' },
  { value: 10000, label: '10kg' },
  { value: 20000, label: '20kg' },
  { value: 30000, label: '30kg (Max)' },
] as const;

export interface ValidationError {
  field: string;
  message: string;
  suggestion?: string;
}

export function validateLabelRequest(request: PostNLLabelRequest): ValidationError[] {
  const errors: ValidationError[] = [];

  const senderPostal = validatePostalCode(
    request.senderInfo.postalCode,
    request.senderInfo.countryCode
  );
  if (!senderPostal.valid) {
    errors.push({
      field: 'senderPostalCode',
      message: senderPostal.error || 'Invalid sender postal code',
      suggestion: senderPostal.format ? `Format: ${senderPostal.format}` : undefined,
    });
  }

  const receiverPostal = validatePostalCode(
    request.receiverInfo.postalCode,
    request.receiverInfo.countryCode
  );
  if (!receiverPostal.valid) {
    errors.push({
      field: 'receiverPostalCode',
      message: receiverPostal.error || 'Invalid receiver postal code',
      suggestion: receiverPostal.format ? `Format: ${receiverPostal.format}` : undefined,
    });
  }

  if (!request.senderInfo.street || request.senderInfo.street.length < 2) {
    errors.push({ field: 'senderStreet', message: 'Sender street name is required' });
  }

  if (!request.senderInfo.houseNumber) {
    errors.push({ field: 'senderHouseNumber', message: 'Sender house number is required' });
  }

  if (!request.senderInfo.city || request.senderInfo.city.length < 2) {
    errors.push({ field: 'senderCity', message: 'Sender city is required' });
  }

  if (!request.receiverInfo.street || request.receiverInfo.street.length < 2) {
    errors.push({ field: 'receiverStreet', message: 'Receiver street name is required' });
  }

  if (!request.receiverInfo.houseNumber) {
    errors.push({ field: 'receiverHouseNumber', message: 'Receiver house number is required' });
  }

  if (!request.receiverInfo.city || request.receiverInfo.city.length < 2) {
    errors.push({ field: 'receiverCity', message: 'Receiver city is required' });
  }

  if (request.options.weightGrams < 1) {
    errors.push({ field: 'weight', message: 'Weight must be at least 1 gram' });
  }

  if (request.options.weightGrams > 30000) {
    errors.push({
      field: 'weight',
      message: 'Weight cannot exceed 30kg (30000 grams)',
      suggestion: 'Consider splitting into multiple shipments',
    });
  }

  const destType = getDestinationType(request.receiverInfo.countryCode);
  const availableServices = getAvailableServiceTypes(request.receiverInfo.countryCode);
  const serviceAvailable = availableServices.some(s => s.value === request.options.serviceType);

  if (!serviceAvailable) {
    errors.push({
      field: 'serviceType',
      message: `${request.options.serviceType} delivery is not available for ${getDestinationLabel(request.receiverInfo.countryCode)} destinations`,
      suggestion: `Available options: ${availableServices.map(s => s.label).join(', ')}`,
    });
  }

  if (requiresCustomsDeclaration(request.receiverInfo.countryCode)) {
    if (!request.customs?.contentDescription) {
      errors.push({
        field: 'customsDescription',
        message: 'Customs content description is required for non-EU shipments',
        suggestion: 'Describe the contents of the package (e.g., "Clothing", "Electronics")',
      });
    }
  }

  return errors;
}

export async function generatePostNLLabel(
  request: PostNLLabelRequest,
  authToken: string
): Promise<PostNLLabelResponse> {
  try {
    const validationErrors = validateLabelRequest(request);
    if (validationErrors.length > 0) {
      const firstError = validationErrors[0];
      return {
        success: false,
        error: firstError.message,
        errorDetails: {
          error: firstError.message,
          code: 'VALIDATION_ERROR',
          field: firstError.field,
          suggestion: firstError.suggestion,
          allErrors: validationErrors,
        },
      };
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);
    const response = await fetch(`${supabaseUrl}/functions/v1/postnl-generate-label`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(request),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    let data;
    try {
      data = await response.json();
    } catch {
      return {
        success: false,
        error: 'Invalid response from PostNL service',
      };
    }

    if (!response.ok) {
      return {
        success: false,
        error: data.error || `HTTP ${response.status}`,
        errorDetails: {
          error: data.error || 'Unknown error',
          code: data.code,
          field: data.field,
          suggestion: data.suggestion,
          postalCodeFormat: data.postalCodeFormat,
          allErrors: data.allErrors,
        },
      };
    }

    return {
      success: true,
      barcode: data.barcode,
      trackingCode: data.trackingCode,
      labelUrl: data.labelUrl,
      labelPdfBase64: data.labelPdfBase64,
      destinationType: data.destinationType,
      productCode: data.productCode,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to generate label',
    };
  }
}

export function downloadLabelPdf(base64Data: string, filename: string): void {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function printLabelPdf(base64Data: string): void {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });

  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank');
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print();
    };
  }
  setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

export async function getShipmentLabels(shipmentId: string, adminId: string) {
  const { data, error } = await supabase
    .from('postnl_labels')
    .select('*')
    .eq('shipment_id', shipmentId)
    .eq('admin_id', adminId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching labels:', error);
    return [];
  }

  return data || [];
}

export function getPostNLTrackingUrl(trackingCode: string, postalCode?: string, countryCode?: string): string {
  let url = `https://postnl.nl/tracktrace/?B=${encodeURIComponent(trackingCode)}`;
  if (postalCode) {
    url += `&P=${encodeURIComponent(postalCode)}`;
  }
  if (countryCode) {
    url += `&D=${encodeURIComponent(countryCode)}`;
  }
  url += '&T=C';
  return url;
}

export function formatPostNLError(errorDetails: PostNLErrorResponse): string {
  let message = errorDetails.error;

  if (errorDetails.suggestion) {
    message += `\n\nSuggestion: ${errorDetails.suggestion}`;
  }

  if (errorDetails.postalCodeFormat) {
    message += `\n\nExpected postal code format: ${errorDetails.postalCodeFormat}`;
  }

  return message;
}
